import React, { useRef, useState } from "react";
import Button from "../Button";
import Input from "../Input";
import AiCost, { FreeMark } from "../assistant/AiCost";
import { MyAiAccess } from "../../types/Ai";
import classes from "./aiFill.module.scss";

export interface AiComposerProps {
    /** The text area's id. */
    id: string;
    /** Names the box for screen readers (the card shows no label). */
    label: string;
    text: string;
    onTextChange: (text: string) => void;
    rows?: number;
    /** Omit for a text-only box; with it, Attach, drop and paste take a file. */
    onFileChange?: (file: File | null) => void;
    file?: File | null;
    /** With it, Attach, drop and paste take several files: the first goes to onFileChange, the rest here. */
    onMoreFiles?: (files: File[]) => void;
    /** Files beyond the first, shown with the first (each removable through onMoreFiles). */
    moreFiles?: File[];
    /** Something to show beside each attached file before sending (e.g. Ask's figures toggle). */
    fileExtra?: (file: File) => React.ReactNode;
    accept?: string;
    attachTitle?: string;
    submitLabel: string;
    submitIcon?: string;
    submitTitle?: string;
    busyLabel?: string;
    isBusy?: boolean;
    /** Ask shows its own steps while it works, so its composer doesn't lock the drawer it sits in. */
    hasOwnProgress?: boolean;
    canSubmit: boolean;
    onSubmit: () => void;
    /**
     * Whether the submit uses AI now. Left out, the button carries the AI mark and credits. `false` (the
     * reader works on this computer for what's in the box) shows "free · on this computer" instead — a
     * read that sends nothing never carries the AI mark (user, 2026-10-15).
     */
    usesAi?: boolean;
    /** Pass when the caller has it (else it's read). */
    aiAccess?: Pick<MyAiAccess, "fastPerDay" | "smartPerDay" | "usedFast" | "usedSmart"> | null;
    aiMode?: "fast" | "smart";
    /** Enter submits, Shift+Enter is a new line. */
    submitOnEnter?: boolean;
    /** Between Attach and the submit (e.g. Fast / Smart). */
    tools?: React.ReactNode;
    /** One quiet line under the card. */
    footnote?: React.ReactNode;
    nativeProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

/**
 * The one composer every AI action uses (user, 2026-10-13: "only attachment
 * button and text area as we do in the chatbot … share component and used
 * everywhere we have AI actions"): a card with the text, the attached file,
 * Attach (or drop / paste a file) and the action button — with the AI mark
 * and credits inside it when the action uses AI.
 */
const AiComposer: React.FC<AiComposerProps> = ({
    id, label, text, onTextChange, rows = 3, onFileChange, file = null, onMoreFiles, moreFiles = [], fileExtra, accept, attachTitle,
    submitLabel, submitIcon, submitTitle, busyLabel, isBusy = false, hasOwnProgress = false, canSubmit, onSubmit,
    usesAi, aiAccess, aiMode = "fast", submitOnEnter = false, tools, footnote, nativeProps,
}) => {
    const picker = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const takesFiles = !!onFileChange;
    // Every AI action's button carries the sparkle and today's credits (user, 2026-10-13); a read on this computer says so instead (2026-10-15).
    const local = usesAi === false;
    const icon = submitIcon ?? (local ? "search" : "stars");

    const pick = (chosen: File | null | undefined) => {
        if (!chosen || !onFileChange) return;
        onFileChange(chosen);
        window.setTimeout(() => document.getElementById(id)?.focus(), 0);
    };
    // several at once: the first is the attached file, the rest wait beside it (added to any already there)
    const pickAll = (list: FileList | null | undefined) => {
        const chosen = Array.from(list ?? []);
        if (!chosen.length) return;
        if (!onMoreFiles) { pick(chosen[0]); return; }
        if (!file) { pick(chosen[0]); onMoreFiles([...moreFiles, ...chosen.slice(1)]); return; }
        onMoreFiles([...moreFiles, ...chosen]);
        window.setTimeout(() => document.getElementById(id)?.focus(), 0);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!submitOnEnter || event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
        event.preventDefault();
        if (canSubmit && !isBusy) onSubmit();
    };

    return (
        <div className={classes.composer}>
            <div className={`${classes.inputCard} ${isDragging ? classes.inputCardDrop : ""}`}
                onKeyDown={handleKeyDown}
                onDragOver={(event) => { if (takesFiles && event.dataTransfer?.types?.includes("Files")) { event.preventDefault(); setIsDragging(true); } }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => { if (!takesFiles) return; event.preventDefault(); setIsDragging(false); pickAll(event.dataTransfer?.files); }}
                onPaste={(event) => { const pasted = event.clipboardData?.files; if (takesFiles && pasted?.length) { event.preventDefault(); pickAll(pasted); } }}>
                {file && (
                    <div className={classes.attached}>
                        <span className={classes.fileChip} title={file.name}>
                            <i className="bi bi-paperclip" aria-hidden="true" /> {file.name}
                        </span>
                        {fileExtra?.(file)}
                        <Button type="button" variant="ghost" size="xs" icon="x-lg" onClick={() => onFileChange?.(null)} title="Remove the attached file">Remove</Button>
                    </div>
                )}
                {file && moreFiles.map((more, index) => (
                    <div key={`${more.name}-${index}`} className={classes.attached}>
                        <span className={classes.fileChip} title={more.name}>
                            <i className="bi bi-paperclip" aria-hidden="true" /> {more.name}
                        </span>
                        {fileExtra?.(more)}
                        <Button type="button" variant="ghost" size="xs" icon="x-lg" onClick={() => onMoreFiles?.(moreFiles.filter((_f, at) => at !== index))} title={`Remove ${more.name}`}>Remove</Button>
                    </div>
                ))}
                <Input type="textarea" label={label} id={id} value={text} rows={rows} nativeProps={nativeProps}
                    onChange={(_, value) => onTextChange(String(value ?? ""))} />
                <div className={classes.inputTools}>
                    {takesFiles && (
                        <>
                            <input ref={picker} type="file" hidden aria-label="File to attach" accept={accept} multiple={!!onMoreFiles}
                                onChange={(event) => { const chosen = event.target.files; pickAll(chosen); event.target.value = ""; }} />
                            <Button type="button" variant="ghost" size="xs" icon="paperclip" onClick={() => picker.current?.click()}
                                title={attachTitle ?? "Attach a file — or drop or paste it here"}>
                                Attach
                            </Button>
                        </>
                    )}
                    {tools}
                    <span className={classes.grow} />
                    <Button type="button" variant="primary" size="sm" icon={icon} onClick={onSubmit} title={submitTitle}
                        loading={isBusy && !hasOwnProgress} disabled={!canSubmit || isBusy}>
                        {isBusy && busyLabel ? busyLabel : submitLabel}
                        {local
                            ? <FreeMark inButton />
                            : (aiAccess !== undefined
                                ? <AiCost access={aiAccess} mode={aiMode} compact inButton noIcon={icon === "stars"} />
                                : <AiCost mode={aiMode} compact inButton noIcon={icon === "stars"} />)}
                    </Button>
                </div>
            </div>
            {footnote && <p className={classes.footnote}>{footnote}</p>}
        </div>
    );
};

export default AiComposer;
